import { useCallback, useMemo } from 'react';
import {
  Controller,
  useFormContext,
  type FieldPathByValue,
  type FieldValues,
} from 'react-hook-form';

import { Select } from '@breezeventures/ui-kit/select';
import { Typography } from '@breezeventures/ui-kit/typography';

export type MultiSelectOption = {
  value: string;
  label: string;
};

type Props<TFieldValues extends FieldValues> = {
  name: FieldPathByValue<TFieldValues, string[]>;
  label: string;
  options: MultiSelectOption[];
  onValueChange?: (value: string[]) => void;
};

export function MultiSelectFilter<TFieldValues extends FieldValues>({
  name,
  label,
  options,
  onValueChange,
}: Props<TFieldValues>) {
  const { control } = useFormContext<TFieldValues>();

  const labelByValue = useMemo(
    () => new Map(options.map((option) => [option.value, option.label])),
    [options]
  );

  const renderValue = useCallback(
    (value: string[]) =>
      value.map((item) => labelByValue.get(item) ?? item).join(', '),
    [labelByValue]
  );

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Select
          multiple
          ref={field.ref}
          value={field.value as string[]}
          label={label}
          labelPosition="inner"
          onBlur={field.onBlur}
          renderValue={renderValue}
          onChange={(value: string[]) => {
            field.onChange(value);
            onValueChange?.(value);
          }}
        >
          {options.map((option) => (
            <Select.Option key={option.value} value={option.value}>
              <Typography.Text>{option.label}</Typography.Text>
              <Select.OptionIndicator multiple />
            </Select.Option>
          ))}
        </Select>
      )}
    />
  );
}
